import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { AnimatePresence, type AnimationProps, motion } from "framer-motion";
import React from "react";

import { ZIndex } from "~/common/constants";

import { ConfirmationDialog, type ConfirmationDialogCloseProps } from "./confirmation-dialog";

const animationProps: AnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.4 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

/** Wide modal shell shared by consultation feedback and price-list notices. */
export function CenteredDialog({
  isOpen,
  onOpenChange,
  children,
  className,
  closeProps,
}: React.PropsWithChildren<{
  isOpen: boolean;
  onOpenChange(newIsOpen: boolean): void;
  className?: string;
  closeProps: ConfirmationDialogCloseProps;
}>) {
  return (
    <AnimatePresence>
      {isOpen ? (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
          <Dialog.Portal>
            <motion.div {...animationProps} className="tw-fixed tw-inset-0" style={{ zIndex: ZIndex.CENTERED_DIALOG }}>
              <Dialog.Overlay className="tw-fixed tw-inset-0 tw-bg-backdrop" />

              <Dialog.Content
                // Radix portals this to <body>, and `RedesignRoot` only marks <html> while a redesign PAGE
                // is mounted — these dialogs open over any route, so the marker travels WITH the panel.
                // Safe in every build: `tokens.css` is part of `main.css`, not the lazy redesign chunk
                // (verified in a flags-off `dist`), so the `--rd-*` vars resolve wherever this lands. It
                // still scopes them to this subtree, so nothing leaks into the legacy page behind it.
                data-redesign-v1=""
                className={clsx(
                  "tw-border tw-border-solid tw-border-border tw-bg-background tw-p-8 tw-font-sans tw-text-foreground tw-shadow-lg",
                  // The dialog fills a mobile screen; a capped, centred panel leaves empty bands above and
                  // below it. The dialog is the task on a
                  // small screen, so it takes the screen; the body scrolls inside it.
                  "tw-fixed tw-inset-0 tw-overflow-y-auto tw-rounded-none",
                  // From `sm` up, use a centred, rounded panel capped at 90vh and `max-w-xl`. (The panel
                  // this replaced went edge-to-edge with square corners below `sm` AND carried a
                  // `min-h-[580px]` that made it tower over its own content.)
                  "sm:tw-inset-auto sm:tw-left-1/2 sm:tw-top-1/2 sm:tw-max-h-[90vh] sm:tw-w-full sm:tw-max-w-xl sm:-tw-translate-x-1/2 sm:-tw-translate-y-1/2 sm:tw-rounded-xl",
                )}
                style={{ zIndex: ZIndex.CENTERED_DIALOG + 1 }}
              >
                {className ? <div className={className}>{children}</div> : children}
                <ConfirmationDialog.Close {...closeProps} />
              </Dialog.Content>
            </motion.div>
          </Dialog.Portal>
        </Dialog.Root>
      ) : null}
    </AnimatePresence>
  );
}
